import {
  Bookmark,
  BookOpen,
  Clapperboard,
  ExternalLink,
  Globe,
  Heart,
  ListVideo,
  Pin,
  Play,
  Sparkles,
  X,
  Youtube,
  type LucideIcon,
} from "lucide-react";
import type { ListStatus, ReadingItem } from "@/shared/api/book";
import { coverKind } from "@/shared/lib/cover";
import { track } from "@/shared/lib/track";
import {
  formatDate,
  LIST_ICON,
  LIST_LABEL,
  recommendLabel,
  submitterName,
} from "@/shared/lib/labels";
import { cn } from "@/shared/lib/utils";
import { Chip } from "@/shared/ui/chip";
import { Flag } from "@/shared/ui/flag";
import { Markdown } from "@/shared/ui/markdown";
import { StatusChip } from "@/shared/ui/state";

const LIST_STATUSES = Object.keys(LIST_LABEL) as ListStatus[];

/** A kép nélküli fedlap ikonja a típus neve szerint. A típusok az adminban szerkeszthetők,
 *  ezért ismeretlen névnél a link fajtája, végül az Ajánlás általános ikonja dönt (ugyanaz,
 *  mint a keresőben). */
const TYPE_ICON: Record<string, LucideIcon> = {
  Webhely: Globe,
  Könyv: BookOpen,
  Film: Clapperboard,
  "YouTube-videó": Youtube,
  "YouTube-csatorna": Youtube,
};

function typeIcon(item: ReadingItem): LucideIcon {
  return (item.item_type && TYPE_ICON[item.item_type.name]) || (item.embed ? Youtube : Bookmark);
}

/** Az Ajánló kártyája (a listák közül egyedül ez kártya — design-rendszer 7.). Minden elem
 *  ugyanúgy épül: 16:9 fedlap · típus, nyelv, kedvenc · cím és szerző-alkotó · leírás
 *  (AI-összefoglalónál halk jelzéssel) · ajánlás · metasor · „Megnyitom" · jelölő-sor.
 *
 *  Média: YouTube-videónál a fedlap koppintásra a helyén betölti a beágyazott lejátszót
 *  (addig a lista nem szól külső szerverhez); egyszerre egy szól — a `playing` és az
 *  `onPlay` a listaoldal állapota. A „Megnyitom" mindig teljes szélességű gomb; a szerzők
 *  ajánlásánál nem írjuk ki, ki vitte fel. */
export function ReadingCard({
  item,
  onToggleList,
  onToggleFavorite,
  busy,
  playing = false,
  onPlay,
}: {
  item: ReadingItem;
  onToggleList?: (next: ListStatus | null) => void;
  onToggleFavorite?: (next: boolean) => void;
  busy?: boolean;
  playing?: boolean;
  onPlay?: (playing: boolean) => void;
}) {
  // A típus a chipben, a nyelv a zászlóban van — ide a két dátum kerül, kiírt címkével,
  // hogy a megjelenés éve és a felkerülés napja ne legyen összetéveszthető.
  const meta = [
    item.published_year ? `Megjelent: ${item.published_year}` : null,
    `Felkerült: ${formatDate(item.created_at)}`,
    item.source === "community" ? `Beküldte: ${submitterName(item.author)}` : null,
  ].filter(Boolean) as string[];

  const embeddable = Boolean(item.embed?.url);
  const showPlayer = playing && embeddable;

  return (
    <article className="flex flex-col gap-2.5 overflow-hidden rounded-lg border border-line p-3.5">
      <div className="-mx-3.5 -mt-3.5">
        {showPlayer ? (
          <EmbedPlayer item={item} />
        ) : (
          <ReadingCover
            item={item}
            onPlay={
              embeddable
                ? () => {
                    track("book.reading.play", item.id);
                    onPlay?.(true);
                  }
                : undefined
            }
          />
        )}
      </div>

      <div className="flex items-start justify-between gap-2">
        {/* A kártya bal felső sora mondja meg, „mi ez", a jobb felső sarok csak a művelet. */}
        <span className="flex flex-wrap items-center gap-1.5 pt-1">
          {item.item_type && <Chip>{item.item_type.name}</Chip>}
          <Flag language={item.language} />
          {item.is_pinned && (
            <Chip variant="neutral" className="gap-1">
              <Pin size={11} strokeWidth={2} aria-hidden />
              Kiemelt
            </Chip>
          )}
          <StatusChip status={item.status} />
        </span>
        <span className="-mr-1.5 -mt-1 flex shrink-0 items-center">
          {showPlayer && (
            <button
              type="button"
              aria-label="Lejátszó bezárása"
              onClick={() => onPlay?.(false)}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-muted hover:text-ink"
            >
              <X size={20} />
            </button>
          )}
          {onToggleFavorite && (
            <button
              type="button"
              aria-label={item.is_favorite ? "Kedvenc törlése" : "Kedvencnek jelölöm"}
              aria-pressed={item.is_favorite}
              disabled={busy}
              onClick={() => onToggleFavorite(!item.is_favorite)}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md disabled:opacity-50"
            >
              <Heart
                size={20}
                className={cn(item.is_favorite ? "text-gold" : "text-muted")}
                fill={item.is_favorite ? "currentColor" : "none"}
              />
            </button>
          )}
        </span>
      </div>

      <div className="flex flex-col gap-0.5">
        <h3 className="text-[16px] font-semibold leading-snug">{item.title}</h3>
        {item.creator && (
          <p className="text-[13px] font-medium leading-snug text-muted">{item.creator}</p>
        )}
        {item.original_title && (
          <p className="text-[12px] leading-snug text-muted">
            Eredeti cím: <span className="italic">{item.original_title}</span>
          </p>
        )}
      </div>
      {item.body && <Markdown className="text-[14px] leading-[1.5]">{item.body}</Markdown>}
      {item.body && item.ai_summary && (
        <p className="-mt-1 flex items-center gap-1.5 text-[11px] leading-snug text-muted">
          <Sparkles size={12} className="shrink-0 text-placeholder" aria-hidden />
          AI-összefoglaló
        </p>
      )}

      {item.recommend_note && (
        <div className="flex flex-col gap-1 border-l-2 border-gold pl-3">
          <span className="text-[12px] font-semibold text-gold-text">
            {recommendLabel(item.source, item.author)}
          </span>
          <Markdown className="text-[13px] leading-[1.5]">{item.recommend_note}</Markdown>
        </div>
      )}
      <p className="text-[12px] text-muted">{meta.join(" · ")}</p>

      {/* A műveletek a kártya aljára tolva: a kéthasábos rácsban egy sorban állnak. */}
      <div className="mt-auto flex flex-col gap-2.5">
        {item.url && (
          <a
            href={item.url}
            target="_blank"
            rel="noreferrer noopener"
            onClick={() => track("book.reading.open", item.id)}
            className="flex min-h-11 items-center justify-center gap-2 rounded-md border-[1.5px] border-gold px-4 text-[14px] font-semibold text-gold-text hover:bg-cream"
          >
            <ExternalLink size={16} />
            {item.embed ? "Megnyitom a YouTube-on" : "Megnyitom"}
          </a>
        )}
        {onToggleList && (
          <ListMarks value={item.list_status} busy={busy} onChange={onToggleList} />
        )}
      </div>
    </article>
  );
}

/** A jelölő-sor: három egyenlő oszlop, ikon a felirat fölött — így ugyanaz a rövid szó fér
 *  ki mobilon és az asztali rács keskeny kártyáján is. Egyszerre egy állapot; a kiválasztott
 *  arany kerettel és `gold-soft` háttérrel emelkedik ki (a felirat nem változik), újra
 *  rákoppintva visszavonható. */
function ListMarks({
  value,
  busy,
  onChange,
}: {
  value: ListStatus | null;
  busy?: boolean;
  onChange: (next: ListStatus | null) => void;
}) {
  return (
    <div
      role="group"
      aria-label="Saját jelölésem"
      className="grid grid-cols-3 gap-2 border-t border-line pt-2.5"
    >
      {LIST_STATUSES.map((status) => {
        const Icon = LIST_ICON[status];
        const active = value === status;
        return (
          <button
            key={status}
            type="button"
            aria-pressed={active}
            disabled={busy}
            onClick={() => onChange(active ? null : status)}
            className={cn(
              "flex min-h-[52px] flex-col items-center justify-center gap-1 rounded-md border px-1 py-1.5 text-[12px] leading-tight transition-colors disabled:opacity-50",
              active
                ? "border-gold bg-gold-soft font-semibold text-ink"
                : "border-line font-medium text-ink-soft hover:bg-paper",
            )}
          >
            <Icon size={16} className={cn("shrink-0", active ? "text-gold-text" : "text-muted")} />
            {LIST_LABEL[status]}
          </button>
        );
      })}
    </div>
  );
}

/** A 16:9 fedlap (`coverKind`). Lejátszható videónál play-gomb; más linknél a kép maga is a
 *  link, de a billentyűzet és a felolvasó elől rejtve, mert alatta ott a „Megnyitom" gomb. */
function ReadingCover({ item, onPlay }: { item: ReadingItem; onPlay?: () => void }) {
  const kind = coverKind(item);
  const thumb = item.thumbnail;
  const Icon = typeIcon(item);
  const box = "relative block aspect-video w-full overflow-hidden";

  const content = (
    <>
      {kind === "fill" && thumb && (
        <img
          src={thumb.url}
          alt=""
          width={thumb.width}
          height={thumb.height}
          loading="lazy"
          className="h-full w-full object-cover"
        />
      )}
      {(kind === "contain" || kind === "channel") && thumb && (
        <>
          <Backdrop src={thumb.url} dim={kind === "channel" ? "bg-ink/50" : "bg-ink/20"} />
          {kind === "contain" ? (
            <span className="absolute inset-0 flex items-center justify-center">
              <img
                src={thumb.url}
                alt=""
                loading="lazy"
                className="h-[86%] w-auto max-w-[90%] rounded-[3px] object-contain shadow-[0_6px_18px_rgba(20,25,46,0.4)]"
              />
            </span>
          ) : (
            <span className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-4 text-center">
              <img
                src={thumb.url}
                alt=""
                loading="lazy"
                className="aspect-square h-[42%] rounded-full object-cover shadow-[0_0_0_3px_rgba(255,255,255,0.95),0_6px_18px_rgba(20,25,46,0.4)]"
              />
              <span className="flex flex-col gap-0.5">
                <span className="text-[15px] font-bold leading-snug text-white">{item.title}</span>
                {item.embed?.handle && (
                  <span className="text-[12px] font-medium text-white/85">{item.embed.handle}</span>
                )}
              </span>
            </span>
          )}
        </>
      )}
      {kind === "empty" && (
        <span className="flex h-full w-full flex-col items-center justify-center gap-2.5 border-b border-line bg-cream">
          <Icon size={40} strokeWidth={1.6} className="text-gold" aria-hidden />
          {item.item_type && (
            <span className="text-[11px] font-extrabold uppercase tracking-[0.12em] text-gold-text">
              {item.item_type.name}
            </span>
          )}
        </span>
      )}
      {item.embed?.kind === "playlist" && (
        <span className="absolute bottom-2 right-2 flex items-center gap-1 rounded bg-ink/80 px-1.5 py-0.5 text-[11px] font-semibold text-white">
          <ListVideo size={13} aria-hidden />
          Lejátszási lista
        </span>
      )}
      {onPlay && (
        <span
          aria-hidden
          className="absolute inset-0 flex items-center justify-center bg-ink/10 transition-colors group-hover:bg-ink/20"
        >
          <span className="flex size-14 items-center justify-center rounded-full bg-white/95 text-ink shadow-md">
            <Play size={26} fill="currentColor" className="ml-0.5" />
          </span>
        </span>
      )}
    </>
  );

  if (onPlay) {
    return (
      <button
        type="button"
        aria-label={`Lejátszás: ${item.title}`}
        onClick={onPlay}
        className={cn(box, "group cursor-pointer bg-ink")}
      >
        {content}
      </button>
    );
  }
  if (item.url) {
    return (
      <a
        href={item.url}
        target="_blank"
        rel="noreferrer noopener"
        tabIndex={-1}
        aria-hidden
        onClick={() => track("book.reading.open", item.id)}
        className={cn(box, "group bg-cream")}
      >
        {content}
      </a>
    );
  }
  return <span className={cn(box, "bg-cream")}>{content}</span>;
}

/** Az álló kép és a csatorna-avatár mögötti elmosott, felnagyított háttér (tiszta CSS). */
function Backdrop({ src, dim }: { src: string; dim: string }) {
  return (
    <>
      <img
        src={src}
        alt=""
        aria-hidden
        loading="lazy"
        className="absolute inset-0 h-full w-full scale-125 object-cover blur-2xl saturate-[1.1]"
      />
      <span aria-hidden className={cn("absolute inset-0", dim)} />
    </>
  );
}

/** A beágyazott YouTube-lejátszó — csak koppintás után kerül a DOM-ba: a `nocookie`
 *  domainről, 16:9-ben, azonnali indítással (a koppintás a felhasználói gesztus). A lista
 *  címében már van lekérdezés (`videoseries?list=…`), ezért a paraméterek `&`-tel folytatják. */
function EmbedPlayer({ item }: { item: ReadingItem }) {
  if (!item.embed?.url) return null;
  const sep = item.embed.url.includes("?") ? "&" : "?";
  return (
    <div className="aspect-video w-full bg-ink">
      <iframe
        src={`${item.embed.url}${sep}autoplay=1&rel=0`}
        title={item.title}
        className="h-full w-full"
        allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
        allowFullScreen
        referrerPolicy="strict-origin-when-cross-origin"
      />
    </div>
  );
}
